import type { LpcLayers } from "../../infra/api.js";
import {
  ACCESSORY_STYLES,
  BODY_COLORS,
  BODY_STYLES,
  FABRIC_COLORS,
  HAIR_COLORS,
  HAIR_STYLES,
  LpcAvatar,
  PANTS_STYLES,
  SHIRT_STYLES,
} from "./LpcAvatar.js";
interface Props {
  layers: LpcLayers;
  onChange: (next: LpcLayers) => void;
  label?: string;
}

function patch<T extends keyof LpcLayers>(
  layers: LpcLayers,
  key: T,
  value: LpcLayers[T],
): LpcLayers {
  return { ...layers, [key]: value };
}

const HAIR_LABELS: Record<string, string> = {
  short: "curto",
  long: "longo",
  ponytail: "rabo",
  bun: "coque",
  mohawk: "mohawk",
  afro: "afro",
  sidecut: "side-cut",
  bald: "careca",
};
const SHIRT_LABELS: Record<string, string> = {
  tshirt: "camiseta",
  longsleeve: "manga longa",
  tank: "regata",
  hoodie: "moletom",
  blazer: "blazer",
  dress: "vestido",
};
const PANTS_LABELS: Record<string, string> = {
  jeans: "jeans",
  shorts: "shorts",
  skirt: "saia",
  cargo: "cargo",
  bellbottom: "boca-de-sino",
};
const ACC_LABELS: Record<string, string> = {
  none: "nenhum",
  glasses: "óculos",
  shades: "escuros",
  hat: "fedora",
  cap: "boné",
  headphones: "fones",
  bandana: "bandana",
  beanie: "gorro",
};

function Swatch({
  selected,
  color,
  onClick,
  title,
}: {
  selected: boolean;
  color: string;
  onClick: () => void;
  title: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`h-7 w-7 transition-all ${
        selected
          ? "ring-2 ring-butter ring-offset-2 ring-offset-tape scale-110"
          : "hover:scale-105 hover:ring-1 hover:ring-paper/40 hover:ring-offset-1 hover:ring-offset-tape"
      }`}
      style={{ background: color, border: "1px solid rgba(0,0,0,0.5)" }}
    />
  );
}

function StyleButton({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest2 transition-colors ${
        selected
          ? "border-butter bg-butter text-asphalt"
          : "border-border text-ink-muted hover:border-paper hover:text-paper"
      }`}
    >
      {label}
    </button>
  );
}

function Section({
  num,
  title,
  children,
}: {
  num: string;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2 border-l-2 border-border pl-4">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[9px] tabular-nums text-vinyl">{num}</span>
        <span className="catalog-num text-paper">{title}</span>
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

export function AvatarBuilder({ layers, onChange, label }: Props): JSX.Element {
  const accId = layers.accessory?.id ?? "none";
  const accColor = layers.accessory?.color ?? "black";

  function setAccessory(id: string, color?: string): void {
    if (id === "none") {
      const { accessory: _accessory, ...rest } = layers;
      onChange(rest);
      return;
    }
    const acc: { id: string; color?: string } = { id };
    if (color) acc.color = color;
    onChange({ ...layers, accessory: acc });
  }

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Preview stage */}
      <div className="col-span-12 flex flex-col items-center gap-3 sm:col-span-4">
        <div
          className="relative w-full overflow-hidden border border-border"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 100%, rgba(240,200,74,0.20) 0%, rgba(0,0,0,0) 70%), linear-gradient(180deg, #1a1108 0%, #0a0805 100%)",
            aspectRatio: "1 / 1",
            maxWidth: 240,
          }}
        >
          <div className="stage-scanlines pointer-events-none absolute inset-0" />
          <div
            className="spotlight pointer-events-none absolute -top-10 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(240,200,74,0.4) 0%, rgba(240,200,74,0) 60%)",
            }}
          />
          <div
            className="absolute inset-x-0 bottom-0 h-6"
            style={{
              background: "linear-gradient(180deg, #3a2010 0%, #0a0604 100%)",
              borderTop: "1px solid #4a2818",
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center pt-4">
            <LpcAvatar layers={layers} size={160} label={label} />
          </div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
          prévia · idle
        </span>
      </div>

      {/* Builder grid */}
      <div className="col-span-12 grid grid-cols-1 gap-5 sm:col-span-8 sm:grid-cols-2">
        <Section num="01" title="Pele">
          <div className="flex gap-2">
            {BODY_STYLES.map((id) => (
              <Swatch
                key={id}
                selected={layers.body.id === id}
                color={BODY_COLORS[id]}
                title={id}
                onClick={() => onChange(patch(layers, "body", { id }))}
              />
            ))}
          </div>
        </Section>

        <Section num="02" title="Cabelo">
          <div className="flex flex-wrap gap-1.5">
            {HAIR_STYLES.map((id) => (
              <StyleButton
                key={id}
                selected={layers.hair.id === id}
                label={HAIR_LABELS[id] ?? id}
                onClick={() => onChange(patch(layers, "hair", { ...layers.hair, id }))}
              />
            ))}
          </div>
          {layers.hair.id !== "bald" && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(HAIR_COLORS).map(([color, hex]) => (
                <Swatch
                  key={color}
                  selected={layers.hair.color === color}
                  color={hex}
                  title={color}
                  onClick={() => onChange(patch(layers, "hair", { ...layers.hair, color }))}
                />
              ))}
            </div>
          )}
        </Section>

        <Section num="03" title="Camisa">
          <div className="flex flex-wrap gap-1.5">
            {SHIRT_STYLES.map((id) => (
              <StyleButton
                key={id}
                selected={layers.shirt.id === id}
                label={SHIRT_LABELS[id] ?? id}
                onClick={() => onChange(patch(layers, "shirt", { ...layers.shirt, id }))}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(FABRIC_COLORS).map(([color, hex]) => (
              <Swatch
                key={`shirt-${color}`}
                selected={layers.shirt.color === color}
                color={hex}
                title={color}
                onClick={() => onChange(patch(layers, "shirt", { ...layers.shirt, color }))}
              />
            ))}
          </div>
        </Section>

        <Section num="04" title="Calça">
          <div className="flex flex-wrap gap-1.5">
            {PANTS_STYLES.map((id) => (
              <StyleButton
                key={id}
                selected={layers.pants.id === id}
                label={PANTS_LABELS[id] ?? id}
                onClick={() => onChange(patch(layers, "pants", { ...layers.pants, id }))}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(FABRIC_COLORS).map(([color, hex]) => (
              <Swatch
                key={`pants-${color}`}
                selected={layers.pants.color === color}
                color={hex}
                title={color}
                onClick={() => onChange(patch(layers, "pants", { ...layers.pants, color }))}
              />
            ))}
          </div>
        </Section>

        <Section num="05" title="Acessório">
          <div className="flex flex-wrap gap-1.5">
            {ACCESSORY_STYLES.map((id) => (
              <StyleButton
                key={id}
                selected={accId === id}
                label={ACC_LABELS[id] ?? id}
                onClick={() => setAccessory(id, id === "none" ? undefined : accColor)}
              />
            ))}
          </div>
          {accId !== "none" && (accId === "cap" || accId === "bandana" || accId === "beanie") && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(FABRIC_COLORS).map(([color, hex]) => (
                <Swatch
                  key={`acc-${color}`}
                  selected={accColor === color}
                  color={hex}
                  title={color}
                  onClick={() => setAccessory(accId, color)}
                />
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
